import React from 'react';
import { ArrowUpDown, ChevronDown, ChevronRight, History, SquarePen, Trash2 } from 'lucide-react';
import { ProfileManager } from '../../../components/inventory/ProfileManager';
import { InventoryRoastProfileButton } from '../../../components/inventory/InventoryRoastProfileButton';
import { formatCurrency } from '../../../utils/format';
import type { InventoryItem } from '../../../types/inventory';
import type { CuppingProfile } from '../../../services/profileService';
import type {
  InventoryTypeFilter,
  RoastProfileContext,
  FinishedGoodsTasteProfileContext,
} from '../../../hooks/inventory-hooks/useInventoryPageState';
import { InventoryItemDetails } from '../InventoryItemDetails';
import { useLanguage } from '../../../features/language/useLanguage';
import { usePermissions } from '../../../hooks/usePermissions';

interface InventoryDesktopTableProps {
  typeFilter: InventoryTypeFilter;
  items: InventoryItem[];
  sortConfig: { key: keyof InventoryItem; direction: 'asc' | 'desc' } | null;
  onSort: (key: keyof InventoryItem) => void;
  expandedRowIds: Set<string>;
  onToggleRow: (id: string) => void;
  currencySymbol: string;
  onRowClick: (item: InventoryItem) => void;
  onEditItem: (item: InventoryItem) => void;
  onOpenHistory: (item: InventoryItem) => void;
  onOpenRoastProfile: (context: RoastProfileContext) => void;
  onOpenTasteProfile: (context: FinishedGoodsTasteProfileContext) => void;
  onRestock: (item: InventoryItem) => void;
  onDeleteItem: (item: InventoryItem) => void;
  canWrite: boolean;
  canDelete: boolean;
  onProfileUpdated: () => void;
}

export const InventoryDesktopTable: React.FC<InventoryDesktopTableProps> = ({
  typeFilter,
  items,
  sortConfig,
  onSort,
  expandedRowIds,
  onToggleRow,
  currencySymbol,
  onRowClick,
  onEditItem,
  onOpenHistory,
  onOpenRoastProfile,
  onOpenTasteProfile,
  onRestock,
  onDeleteItem,
  canWrite,
  canDelete,
  onProfileUpdated,
}) => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  // Taste-profile add button is gated separately from inventory:write.
  const canWriteTasteProfile = can('taste_profiles', 'write');
  const getStockUnitLabel = (item: InventoryItem) => (
    item.itemType === 'finished_good' ? t('inventory.basicInfo.units.pcs') : item.unitOfMeasure
  );
  const getDerivedStockStatus = (item: InventoryItem): 'available' | 'low' | 'depleted' => {
    const stock = typeof item.stock === 'number' ? item.stock : 0;
    if (stock <= 0) return 'depleted';
    if (item.itemType === 'finished_good') {
      if (stock <= 10) return 'low';
      return 'available';
    }
    const reorderPoint = typeof item.reorderPoint === 'number' ? item.reorderPoint : 0;
    if (reorderPoint > 0 && stock <= reorderPoint) return 'low';
    return 'available';
  };

  const renderStatusBadge = (item: InventoryItem) => {
    const derived = getDerivedStockStatus(item);
    let statusLabel = t('inventory.table.status.available');
    let statusClass = 'bg-green-100 text-green-800';
    if (derived === 'depleted') {
      statusLabel = t('inventory.table.status.depleted');
      statusClass = 'bg-red-100 text-red-800';
    } else if (derived === 'low') {
      statusLabel = t('inventory.table.status.low');
      statusClass = 'bg-yellow-100 text-yellow-800';
    }
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusClass}`}>
        {statusLabel}
      </span>
    );
  };

  return (
    <div className="hidden md:block bg-white shadow overflow-hidden rounded-lg overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => onSort('name')}
            >
              <div className="flex items-center gap-2">
                {t('forms.customer.itemName')}
                {sortConfig?.key === 'name' && <ArrowUpDown className="h-4 w-4" />}
              </div>
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => onSort('itemType')}
            >
              <div className="flex items-center gap-2">
                {t('inventory.table.columns.type')}
                {sortConfig?.key === 'itemType' && <ArrowUpDown className="h-4 w-4" />}
              </div>
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => onSort('stock')}
            >
              <div className="flex items-center gap-2">
                {t('inventory.table.columns.stock')}
                {sortConfig?.key === 'stock' && <ArrowUpDown className="h-4 w-4" />}
              </div>
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => onSort('averagePrice')}
            >
              <div className="flex items-center gap-2">
                {typeFilter === 'finished_good' ? t('inventory.table.columns.costOfGoodsSold') : t('inventory.table.columns.averagePrice')}
                {sortConfig?.key === 'averagePrice' && <ArrowUpDown className="h-4 w-4" />}
              </div>
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => onSort('estimatedSellingPrice' as keyof InventoryItem)}
            >
              <div className="flex items-center gap-2">
                {t('inventory.table.columns.estimatedSellingPrice')}
                {sortConfig?.key === 'estimatedSellingPrice' && <ArrowUpDown className="h-4 w-4" />}
              </div>
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              {t('inventory.table.columns.stockStatus')}
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => onSort('expiryDate' as keyof InventoryItem)}
            >
              <div className="flex items-center gap-2">
                {t('inventory.lotDetail.fields.expiryDate')}
                {sortConfig?.key === 'expiryDate' && <ArrowUpDown className="h-4 w-4" />}
              </div>
            </th>
            {typeFilter !== 'finished_good' && (
              <>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('inventory.roastedBulk.columns.beanProfile')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('inventory.roastedBulk.columns.roastProfile')}
                </th>
              </>
            )}
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('inventory.roastedBulk.columns.cuppingProfile')}
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => onSort('status')}
            >
              <div className="flex items-center gap-2">
                {t('forms.common.status')}
                {sortConfig?.key === 'status' && <ArrowUpDown className="h-4 w-4" />}
              </div>
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('settingsMcp.table.actions')}</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {items.map((item) => {
            const isExpanded = expandedRowIds.has(item.id);
            const derivedStockStatus = getDerivedStockStatus(item);
            const finishedGoodsTasteProfileExists =
              item.itemType === 'finished_good' &&
              item.cuppingProfiles?.some((p: CuppingProfile) =>
                p.type === 'ItemType' &&
                (p.cupping_type === 'FinishedGoodsTaste' || p.extended_data?.profile_variant === 'finished_goods_simple')
              );

            return (
              <React.Fragment key={item.id}>
                <tr
                  data-test-id={`inventory-item-row-${item.id}`}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => onRowClick(item)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      {item.itemType !== 'roasted_bulk' && (
                        <button
                          data-test-id={`inventory-expand-button-${item.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleRow(item.id);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      )}
                      <div className="flex flex-col leading-snug">
                        <span>{item.name}</span>
                        {item.code && (
                          <span className="text-gray-400 text-[11px] font-normal">
                            {item.code}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                    {item.itemType.replace('_', ' ').toUpperCase()}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm cursor-pointer hover:underline ${
                      derivedStockStatus === 'available' ? 'text-gray-500' : 'text-red-600 font-medium'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenHistory(item);
                    }}
                    title={t('inventory.table.actions.clickViewHistory')}
                  >
                    {(typeof item.stock === 'number' ? item.stock : 0)} {getStockUnitLabel(item)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {typeof item.averagePrice === 'number' ? formatCurrency(item.averagePrice, currencySymbol) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.itemType === 'roasted_bulk' || item.itemType === 'green_bean' ? (
                      <span className="text-gray-400">{t('inventory.modal.values.na')}</span>
                    ) : typeof item.estimatedSellingPrice === 'number' ? (
                      formatCurrency(item.estimatedSellingPrice, currencySymbol)
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{renderStatusBadge(item)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : t('settingsMcp.values.none')}
                  </td>
                  {typeFilter === 'finished_good' ? (
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div
                        className={`flex items-center gap-1.5 ${
                          finishedGoodsTasteProfileExists ? 'justify-center' : 'justify-center'
                        }`}
                      >
                        {canWriteTasteProfile && (
                          <button
                            type="button"
                            data-test-id={`inventory-fg-taste-profile-add-${item.id}`}
                            onClick={e => {
                              e.stopPropagation();
                              onOpenTasteProfile({ item, mode: 'add' });
                            }}
                            className="px-3 py-1 rounded-md text-sm font-medium transition-colors bg-gray-300 text-gray-700/50 hover:bg-gray-400"
                          >
                            {t('inventory.table.actions.add')}
                          </button>
                        )}
                        {finishedGoodsTasteProfileExists && (
                          <button
                            type="button"
                            data-test-id={`inventory-fg-taste-profile-view-${item.id}`}
                            onClick={e => {
                              e.stopPropagation();
                              onOpenTasteProfile({ item, mode: 'view' });
                            }}
                            className="px-3 py-1 rounded-md text-sm font-medium transition-colors bg-primary-500 text-white hover:bg-primary-700"
                            >
                              {t('reports.shift.view')}
                          </button>
                        )}
                      </div>
                    </td>
                  ) : (
                    <>
                      {item.itemType !== 'finished_good' && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <ProfileManager
                              entityType="ItemType"
                              entityId={item.id}
                              profileType="greenBean"
                              existingProfile={item.greenBeanProfiles?.find(p => p.type === 'ItemType')}
                              label={item.greenBeanProfiles?.some(p => p.type === 'ItemType') ? t('reports.shift.view') : undefined}
                              onProfileUpdate={onProfileUpdated}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <InventoryRoastProfileButton
                              item={item}
                              onClick={() =>
                                onOpenRoastProfile({
                                  itemId: item.id,
                                  itemName: item.name,
                                  workOrderId: item.woReferenceId,
                                })
                              }
                              readOnly={!canWrite}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <ProfileManager
                              entityType="ItemType"
                              entityId={item.id}
                              profileType="cupping"
                              existingProfile={item.cuppingProfiles?.find((p: CuppingProfile) => p.type === 'ItemType')}
                              label={item.cuppingProfiles?.some((p: CuppingProfile) => p.type === 'ItemType') ? t('reports.shift.view') : undefined}
                              onProfileUpdate={onProfileUpdated}
                            />
                          </td>
                        </>
                      )}
                      {item.itemType === 'finished_good' && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-gray-400 text-xs">{t('inventory.modal.values.na')}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-gray-400 text-xs">{t('inventory.modal.values.na')}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center gap-1.5 justify-center">
                              {canWriteTasteProfile && (
                                <button
                                  type="button"
                                  data-test-id={`inventory-fg-taste-profile-add-${item.id}`}
                                  onClick={e => {
                                    e.stopPropagation();
                                    onOpenTasteProfile({ item, mode: 'add' });
                                  }}
                                  className="px-3 py-1 rounded-md text-sm font-medium transition-colors bg-gray-300 text-gray-700/50 hover:bg-gray-400"
                                >
                                  {t('inventory.table.actions.add')}
                                </button>
                              )}
                              {finishedGoodsTasteProfileExists && (
                                <button
                                  type="button"
                                  data-test-id={`inventory-fg-taste-profile-view-${item.id}`}
                                  onClick={e => {
                                    e.stopPropagation();
                                    onOpenTasteProfile({ item, mode: 'view' });
                                  }}
                                  className="px-3 py-1 rounded-md text-sm font-medium transition-colors bg-primary-500 text-white hover:bg-primary-700"
                                >
                                  {t('reports.shift.view')}
                                </button>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 uppercase inline-flex text-xs leading-5 font-semibold rounded-lg ${
                        item.status === 'active' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {canWrite && (
                        <>
                          <button
                            data-test-id={`inventory-edit-item-button-${item.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditItem(item);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
                          >
                            <SquarePen className="w-4 h-4" />
                            {t('purchasing.supplierList.actions.edit')}
                          </button>
                          {derivedStockStatus !== 'available' && (
                            <button
                              data-test-id={`inventory-restock-item-button-${item.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRestock(item);
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-md hover:bg-green-100 transition-colors"
                            >
                              {t('inventory.table.actions.restock')}
                            </button>
                          )}
                        </>
                      )}
                      {canDelete && (
                          <button
                            data-test-id={`inventory-delete-item-button-${item.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteItem(item);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            {t('purchasing.supplierList.actions.delete')}
                          </button>
                      )}
                      <button
                        data-test-id={`inventory-history-item-button-${item.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenHistory(item);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                      >
                        <History className="w-4 h-4" />
                        {t('inventory.roastedBulk.history')}
                      </button>
                    </div>
                  </td>
                </tr>
                {isExpanded && item.itemType !== 'roasted_bulk' && (
                  <tr className="bg-gray-50">
                    <td colSpan={typeFilter === 'finished_good' ? 11 : 13} className="px-6 py-4">
                      <InventoryItemDetails
                        key={`${item.id}-${item.stock}-${item.status}`}
                        item={item}
                        canWrite={canWrite}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
